

















































import { Component, Prop, Vue } from "vue-property-decorator";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  addTemplateField,
  appendTemplateField,
  availableTemplateFields,
  insertTemplateField,
  moveTemplateField,
  moveTemplateFieldToEnd,
  removeTemplateField,
} from "@/utils/TemplateFieldOrdering";

/**
 * The chip row that orders the parts of a template, by dragging or by tapping.
 *
 * Shared by the automatic project names (`ProjectSettings.vue`) and the address format
 * (`CompanySettings.vue`). The ordering rules themselves live in
 * [TemplateFieldOrdering](../../utils/TemplateFieldOrdering.ts) so they can be tested without a
 * component: this repo's `@vue/test-utils` is the Vue 3 build and cannot mount a Vue 2 component.
 *
 * Field values are plain strings so an enum of any kind can be edited here. The component owns no
 * template state: every change is emitted, and the parent decides what to keep.
 */
@Component
export default class TemplateFieldEditorComponent extends Vue {
  /** The configured order. */
  @Prop({ type: Array, required: true }) readonly value!: string[];

  /** Every field this template may contain, in the order the available row should offer them. */
  @Prop({ type: Array, required: true }) readonly fields!: string[];

  /**
   * The one field that may appear more than once, and never at either end (a comma).
   *
   * Null for a template with no such field, which is what makes this component reusable.
   */
  @Prop({ type: String, default: null }) readonly repeatableField!: string | null;

  /** Translates a field value for display. */
  @Prop({ type: Function, required: true }) readonly fieldLabel!: (field: string) => string;

  /** i18n key of the message shown when the repeatable field is dropped at either end. */
  @Prop({ type: String, required: true }) readonly edgeErrorKey!: string;

  @Prop({ type: String, required: true }) readonly availableFieldsLabel!: string;

  @Prop({ type: Boolean, default: false }) readonly readonly!: boolean;

  dragSourceIndex: number | null = null;
  dragSourceField: string | null = null;
  dragOverIndex: number | null = null;

  get availableFields(): string[] {
    return availableTemplateFields(this.fields, this.value, this.repeatableField);
  }

  onDragStart(index: number, event: DragEvent) {
    this.dragSourceIndex = index;
    this.dragSourceField = null;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
    }
  }

  onAvailableDragStart(field: string, event: DragEvent) {
    this.dragSourceField = field;
    this.dragSourceIndex = null;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "copy";
    }
  }

  onDragOver(index: number) {
    this.dragOverIndex = index;
  }

  onDragLeave() {
    this.dragOverIndex = null;
  }

  onDragOverContainer() {
    if (this.dragSourceField !== null || this.dragSourceIndex !== null) {
      this.dragOverIndex = this.value.length;
    }
  }

  onDrop(index: number) {
    if (this.dragSourceField !== null) {
      this.applyOrRefuse(
        insertTemplateField(this.value, this.dragSourceField, index, this.repeatableField)
      );
    } else if (this.dragSourceIndex !== null) {
      this.applyOrRefuse(
        moveTemplateField(this.value, this.dragSourceIndex, index, this.repeatableField)
      );
    }
    this.resetDragState();
  }

  /** Dropping past the last chip, which means appending. */
  onDropContainer() {
    if (this.dragSourceField !== null) {
      this.applyOrRefuse(
        appendTemplateField(this.value, this.dragSourceField, this.repeatableField)
      );
    } else if (this.dragSourceIndex !== null) {
      this.applyOrRefuse(
        moveTemplateFieldToEnd(this.value, this.dragSourceIndex, this.repeatableField)
      );
    }
    this.resetDragState();
  }

  onDragEnd() {
    this.resetDragState();
  }

  addField(field: string) {
    if (!field) {
      return;
    }

    this.applyOrRefuse(addTemplateField(this.value, field, this.repeatableField));
  }

  removeField(index: number) {
    this.$emit("input", removeTemplateField(this.value, index, this.repeatableField));
  }

  /** Null is a move the rules refuse, and the crew has to be told which rule. */
  private applyOrRefuse(fields: string[] | null) {
    if (fields === null) {
      SnackbarModule.showError({ key: this.edgeErrorKey });
      return;
    }

    this.$emit("input", fields);
  }

  private resetDragState() {
    this.dragSourceIndex = null;
    this.dragSourceField = null;
    this.dragOverIndex = null;
  }
}
